import React from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { formatCount } from './helpers';
import { RecordIndicator } from './types';
import './index.css';

interface RecordsCreatedIndicatorsProps {
  /**
   * Indicators to render, in display order. Build from a backend
   * `childrenRecords` payload with `buildRecordIndicators`, or supply any
   * custom list — the component itself is agnostic to record types.
   */
  indicators: RecordIndicator[];
  /**
   * Tooltip orientation. `true` places the arrow at the tooltip's bottom-right
   * (`topRight`, used on the Work Orders row); the default places it at the
   * bottom-left (`topLeft`). In both cases a horizontal `align.offset` nudges
   * the popup so the corner arrow's tip lands on the icon center (Figma).
   */
  tooltipArrowBottomRight?: boolean;
}

// Horizontal shift (px) applied to the popup so the corner arrow points at the
// 24px icon's center. Tuned against the live app + verified with Playwright.
const ARROW_ALIGN_OFFSET = 12;

const RecordsCreatedIndicators: React.FC<RecordsCreatedIndicatorsProps> = ({
  indicators,
  tooltipArrowBottomRight = false
}) => {
  if (indicators.length === 0) return null;

  return (
    <div
      className="records-created-indicators"
      data-testid="records-created-indicators"
    >
      {indicators.map(({ key, icon: Icon, label, count, variantClassName }) => {
        const tooltipTitle = `${label}: ${count}`;

        return (
          <Tooltip
            key={key}
            title={tooltipTitle}
            placement={tooltipArrowBottomRight ? 'topRight' : 'topLeft'}
            autoAdjustOverflow={false}
            overlayClassName="records-created-indicator-tooltip"
            mouseLeaveDelay={0}
            align={{
              offset: [
                tooltipArrowBottomRight
                  ? ARROW_ALIGN_OFFSET
                  : -ARROW_ALIGN_OFFSET,
                0
              ]
            }}
            color="#232630"
          >
            <div
              className={`records-created-indicator${
                variantClassName ? ` ${variantClassName}` : ''
              }`}
              data-testid={`records-created-indicator-${key}`}
            >
              {/* Inner wrapper carries the hover raise so the outer element's
                  hit area stays put — see index.css. */}
              <div className="records-created-indicator__lift">
                <Icon
                  className="records-created-indicator__icon"
                  width={24}
                  height={24}
                  aria-label={tooltipTitle}
                />
                <span className="records-created-indicator__counter">
                  {formatCount(count)}
                </span>
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default RecordsCreatedIndicators;
